/**
 * SECURE DATA SERVICE
 * 
 * Servicio de alto nivel para encriptar/desencriptar datos sensibles.
 * Abstrae la gestión de claves y proporciona una API simple.
 * 
 * CUMPLIMIENTO: GDPR Art. 32, HIPAA Security Rule
 */

import { credentialsService } from '@/core/services';
import { encryptionService } from './encryption.service';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('SecureDataService');

// Nombre de la credencial que contiene la Master Key
const MASTER_KEY_CREDENTIAL = 'ENCRYPTION_MASTER_KEY';

// Cache de la master key para evitar llamadas repetidas
let cachedMasterKey: string | null = null;

class SecureDataService {
  
  /**
   * Obtiene la Master Key desde el sistema de credenciales.
   * La cachea en memoria para evitar llamadas repetidas.
   */
  private async getMasterKey(): Promise<string> {
    if (cachedMasterKey) {
      return cachedMasterKey;
    }

    try {
      const masterKey = await credentialsService.getValue(MASTER_KEY_CREDENTIAL);

      if (!masterKey) {
        // Use fallback key if credential not found
        // TODO: Remove this fallback once production key is properly configured
        const devKey = 'DEV_FALLBACK_KEY_CHANGE_IN_PRODUCTION_32CHARS!';
        logger.warn('ENCRYPTION_MASTER_KEY not found in credentials, using fallback key');
        cachedMasterKey = devKey;
        return devKey;
      }

      cachedMasterKey = masterKey;
      return masterKey;
    } catch (error) {
      // Use fallback key if credentials fetch fails
      // TODO: Remove this fallback once production key is properly configured
      const devKey = 'DEV_FALLBACK_KEY_CHANGE_IN_PRODUCTION_32CHARS!';
      logger.warn('Failed to fetch ENCRYPTION_MASTER_KEY, using fallback', error as Error);
      cachedMasterKey = devKey;
      return devKey;
    }
  }

  /**
   * Encripta texto sensible (notas de sesión, diagnósticos, etc.)
   * 
   * @param plaintext - Texto a encriptar
   * @param userId - ID del usuario (para derivar clave única)
   * @returns Objeto con datos encriptados o texto original si falla
   */
  async encryptSensitiveText(plaintext: string, userId: string): Promise<{
    encrypted: boolean;
    data: string;
    metadata?: {
      iv: string;
      authTag: string;
      encryptedAt: string;
      keyVersion: string;
    };
  }> {
    // Si el texto está vacío, no encriptar
    if (!plaintext || plaintext.trim() === '') {
      return { encrypted: false, data: plaintext };
    }

    try {
      const masterKey = await this.getMasterKey();
      const encryptedField = await encryptionService.encrypt(plaintext, userId, masterKey);
      
      logger.debug('Data encrypted successfully', { userId });
      
      return {
        encrypted: true,
        data: encryptedField.encryptedData,
        metadata: {
          iv: encryptedField.iv,
          authTag: encryptedField.authTag,
          encryptedAt: encryptedField.encryptedAt,
          keyVersion: encryptedField.keyVersion
        }
      };
    } catch (error) {
      logger.error('Encryption failed - blocking operation to protect sensitive data', error as Error);
      throw new Error('Encryption required but failed. Cannot store sensitive data unencrypted.');
    }
  }

  /**
   * Desencripta texto sensible
   * 
   * @param encryptedData - Datos encriptados o texto plano
   * @param userId - ID del usuario
   * @param metadata - Metadatos de encriptación (iv, authTag)
   * @returns Texto desencriptado
   */
  async decryptSensitiveText(
    encryptedData: string,
    userId: string,
    metadata?: {
      iv: string;
      authTag: string;
      encryptedAt?: string;
      keyVersion?: string;
    }
  ): Promise<string> {
    // Si no hay metadata, el texto no estaba encriptado
    if (!metadata || !metadata.iv || !metadata.authTag) {
      return encryptedData;
    }

    try {
      const masterKey = await this.getMasterKey();
      
      const decrypted = await encryptionService.decrypt(
        {
          encryptedData,
          iv: metadata.iv,
          authTag: metadata.authTag,
          encryptedBy: userId,
          encryptedAt: metadata.encryptedAt || '',
          keyVersion: metadata.keyVersion || '1.0.0'
        },
        userId,
        masterKey
      );
      
      logger.debug('Data decrypted successfully', { userId });
      return decrypted;
    } catch (error) {
      logger.error('Decryption failed', error as Error);
      // En caso de error, devolver los datos encriptados (mejor que perder todo)
      return '[Error desencriptando - contacte soporte]';
    }
  }

  /**
   * Verifica si los datos están encriptados
   */
  isEncrypted(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false;
    const d = data as Record<string, unknown>;
    const meta = d.metadata as Record<string, unknown> | undefined;
    return d.encrypted === true && !!meta?.iv && !!meta?.authTag;
  }

  /**
   * Limpia la cache de la master key (útil para rotación)
   */
  clearKeyCache(): void {
    cachedMasterKey = null;
    logger.info('Master key cache cleared');
  }
}

export const secureDataService = new SecureDataService();




